import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type {
  BloodParameterDefinition,
  BloodParameterReadingWithDefinition,
  CreateBloodParameterReadingPayload,
  UpdateBloodParameterReadingPayload,
  CreateBloodParameterDefinitionPayload,
} from '../types/blood-parameters';

// =====================================================
// Blood Parameter Definitions Hooks
// =====================================================

async function fetchDefinitions(
  accessToken: string | null | undefined,
  category?: string,
) {
  const params = new URLSearchParams();
  if (category) {
    params.append('category', category);
  }
  const queryString = params.toString();
  const url = `/api/blood-parameters/definitions${queryString ? `?${queryString}` : ''}`;

  return apiRequest<BloodParameterDefinition[]>(url, { method: 'GET' }, accessToken);
}

export function useBloodParameterDefinitions(category?: string) {
  const { session } = useAuth();

  return useQuery<BloodParameterDefinition[]>({
    queryKey: ['blood-parameter-definitions', category],
    enabled: Boolean(session),
    queryFn: () => fetchDefinitions(session?.access_token, category),
    staleTime: 5 * 60 * 1000, // Definitions change rarely, cache for 5 minutes
  });
}

export function useCreateBloodParameterDefinition() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBloodParameterDefinitionPayload) => {
      return apiRequest<BloodParameterDefinition>(
        '/api/blood-parameters/definitions',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blood-parameter-definitions'],
      });
    },
  });
}

// =====================================================
// Blood Parameter Readings Hooks
// =====================================================

async function fetchReadings(
  clientId: string,
  accessToken: string | null | undefined,
) {
  return apiRequest<BloodParameterReadingWithDefinition[]>(
    `/api/clients/${clientId}/blood-parameters`,
    { method: 'GET' },
    accessToken,
  );
}

export function useClientBloodParameters(clientId: string) {
  const { session } = useAuth();

  return useQuery<BloodParameterReadingWithDefinition[]>({
    queryKey: ['blood-parameters', clientId],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchReadings(clientId, session?.access_token),
  });
}

export function useCreateBloodParameterReading(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBloodParameterReadingPayload) => {
      return apiRequest<BloodParameterReadingWithDefinition>(
        `/api/clients/${clientId}/blood-parameters`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blood-parameters', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['timeline', clientId],
      });
    },
  });
}

export function useUpdateBloodParameterReading(
  clientId: string,
  readingId: string,
) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateBloodParameterReadingPayload) => {
      return apiRequest<BloodParameterReadingWithDefinition>(
        `/api/clients/${clientId}/blood-parameters/${readingId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blood-parameters', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['timeline', clientId],
      });
    },
  });
}

export function useDeleteBloodParameterReading(
  clientId: string,
  readingId: string,
) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiRequest<void>(
        `/api/clients/${clientId}/blood-parameters/${readingId}`,
        {
          method: 'DELETE',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['blood-parameters', clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['timeline', clientId],
      });
    },
  });
}

export function useInvalidateBloodParameters(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['blood-parameters', clientId],
    });
    queryClient.invalidateQueries({
      queryKey: ['timeline', clientId],
    });
  };
}

// =====================================================
// Helper: Group readings by parameter
// =====================================================

import type { GroupedBloodParameter, BloodParametersByCategory } from '../types/blood-parameters';

export function groupReadingsByParameter(
  readings: BloodParameterReadingWithDefinition[],
): GroupedBloodParameter[] {
  const groupMap = new Map<string, GroupedBloodParameter>();

  for (const reading of readings) {
    // Determine the grouping key (definition ID or custom name)
    const key = reading.parameter_definition_id ?? `custom:${reading.custom_parameter_name}`;

    if (!groupMap.has(key)) {
      const displayName =
        reading.parameter?.display_name ?? reading.custom_parameter_name ?? 'Unknown';
      const category = reading.parameter?.category ?? 'Custom';
      const defaultUnit = reading.parameter?.default_unit ?? reading.unit;
      const referenceRange = reading.parameter?.reference_range ?? null;

      groupMap.set(key, {
        definition: reading.parameter ?? null,
        custom_name: reading.custom_parameter_name,
        display_name: displayName,
        category,
        default_unit: defaultUnit,
        reference_range: referenceRange,
        readings: [],
        latest_reading: reading,
      });
    }

    const group = groupMap.get(key)!;
    group.readings.push(reading);

    // Update latest reading if this one is more recent
    if (new Date(reading.test_date) > new Date(group.latest_reading.test_date)) {
      group.latest_reading = reading;
    }
  }

  // Sort readings within each group by date (newest first)
  for (const group of groupMap.values()) {
    group.readings.sort(
      (a, b) => new Date(b.test_date).getTime() - new Date(a.test_date).getTime(),
    );
  }

  // Sort groups by category and display name
  return Array.from(groupMap.values()).sort((a, b) => {
    // First sort by category
    const categoryCompare = a.category.localeCompare(b.category);
    if (categoryCompare !== 0) return categoryCompare;
    // Then by display name
    return a.display_name.localeCompare(b.display_name);
  });
}

export function groupByCategory(
  groupedParameters: GroupedBloodParameter[],
): BloodParametersByCategory[] {
  const categoryMap = new Map<string, BloodParametersByCategory>();

  for (const param of groupedParameters) {
    if (!categoryMap.has(param.category)) {
      categoryMap.set(param.category, {
        category: param.category,
        parameters: [],
      });
    }
    categoryMap.get(param.category)!.parameters.push(param);
  }

  // Sort categories (put Custom at the end)
  return Array.from(categoryMap.values()).sort((a, b) => {
    if (a.category === 'Custom') return 1;
    if (b.category === 'Custom') return -1;
    return a.category.localeCompare(b.category);
  });
}
